import React, { useEffect } from 'react';
import classnames from 'classnames';
import { FCP } from 'types';

import Ratings from 'components/ratings';
import ZpButton from 'components/zpButton';
import { reviewResponse } from 'services/jobReviewService';
import {
  COMPANY_REVIEW_QUESTION_IDS,
  COMPANY_REVIEW_RATING_CATEGORIES,
  JOB_TITLE_REVIEW_QUESTION_IDS,
  JOB_TITLE_REVIEW_RATING_CATEGORIES,
  MODERATION_STATUS_LIST,
} from 'utils/constant';

import classes from './anonReviewForm.module.css';

interface formattedResponse {
  questionId: string;
  answers: string[];
}

interface Props extends FCP {
  className?: string;
  companyName?: string;
  companyID?: string;
  jobTitle?: string;
  jobTitleArticle?: string;
  popup?: boolean;
}

const AnonReviewForm = ({
  className,
  companyName,
  companyID,
  jobTitle,
  jobTitleArticle,
  popup,
}: Props) => {
  const [responses, setResponses] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [canSubmit, setCanSubmit] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [answers, setAnswers] = React.useState({});
  const [error, setError] = React.useState('');
  const categories = jobTitle
    ? JOB_TITLE_REVIEW_RATING_CATEGORIES
    : COMPANY_REVIEW_RATING_CATEGORIES;
  const QUESTION_IDS = jobTitle ? JOB_TITLE_REVIEW_QUESTION_IDS : COMPANY_REVIEW_QUESTION_IDS;

  useEffect(() => {
    // allow blank text responses, require all star ratings
    const allRatingsProvided = categories.every((category) => responses[category.id]);
    if (allRatingsProvided) setCanSubmit(allRatingsProvided);
  }, [responses]);

  const checkLength = () => {
    const length = Object.values(answers).reduce(
      (prev: number, cur: string) => prev + cur.length,
      0
    );
    return Number(length) >= 75;
  };

  const validate = () => {
    // if length of all answer combined is less than 75 chars than show error.
    if (!checkLength()) {
      setError('Please enter at least 75 characters in this fields');
      return false;
    }

    setError('');
    return true;
  };

  const handleChange = (value, field, isAnswer = false) => {
    validate();
    // if value is empty, remove the key from responses
    if (!value) {
      const { [QUESTION_IDS[field]]: _, ...rest } = responses;
      setResponses(rest);
      return;
    }
    isAnswer && setAnswers({ ...answers, [field]: value });
    setResponses({ ...responses, [QUESTION_IDS[field]]: value });
  };

  const handleRating = (rating, questionId) => {
    setResponses({ ...responses, [questionId]: (rating + 1).toString() });
  };

  const companyReviewPayload = {
    entityType: 'COMPANY',
    entityValue: companyID,
    reviewCategory: 'USER_REVIEWS',
    source: 'company-reviews-page',
    surveyId: '5b5045f1a1a4f037c3641c20',
  };

  const jobTitleReviewPayload = {
    responses,
    entityType: 'JOB_TITLE',
    entityValue: jobTitle,
    reviewCategory: 'JOB_TITLE_REVIEWS',
    surveyId: '5c2e97943a2af726c0319dd0',
    urlQueryString: `?surveyId=109&jobTitle=${jobTitle}&u=43664237849`,
    source: 'career-main-page',
  };

  const onSubmit = async () => {
    if (!validate()) {
      return;
    }
    // api route expects an array of objects with questionID, and an array of answers
    const formattedResponses: formattedResponse[] = Object.keys(responses).map((questionId) => ({
      questionId,
      answers: [responses[questionId]],
    }));

    const reviewPayload = {
      ...(jobTitle ? jobTitleReviewPayload : companyReviewPayload),
      responses: formattedResponses,
    };

    if (jobTitle) {
      reviewPayload.responses = [
        ...reviewPayload.responses,
        {
          answers: [jobTitle],
          questionId: '5c2e9695858f55874fb627a8',
        },
      ];
    } else if (
      formattedResponses.length === categories.length &&
      categories.every((category) => responses[category.id])
    ) {
      reviewPayload.status = MODERATION_STATUS_LIST.approved;
    }

    try {
      const response = await reviewResponse(reviewPayload);
      if (response?.data?.success) {
        return setSubmitted(true);
      }
      throw response?.data || response;
    } catch (error) {
      // Error messages are setup at /services/userActivity/userActivityService.js.
      if (error?.errCode === 429) {
        setSubmitError(error.errText);
      }
      console.error(error);
    }
  };

  const getTitle = () => {
    if (jobTitle) {
      return `Working as ${jobTitleArticle} ${jobTitle}?`;
    }
    return `Work at ${companyName}? Share your Experience`;
  };

  const renderQuestionBox = ({ name, label }) => {
    return (
      <div className="z-mb-20">
        <label className="z-mb-8">{label}</label>
        <input
          type="text"
          className={classnames('z-px-16 w-100 rounded', classes['answer-input'])}
          onChange={(e) => handleChange(e.target.value, name, true)}
        />
      </div>
    );
  };

  const getCompanyQuestions = () => {
    const questions = [
      {
        name: 'pros',
        label: `Pros of working at ${companyName}`,
      },
      {
        name: 'cons',
        label: `Cons of working at ${companyName}`,
      },
      {
        name: 'benefits',
        label: `What's Your Favorite Perk / Benefit?`,
      },
    ];

    return questions.map(renderQuestionBox);
  };

  const getJobTitleQuestions = () => {
    const questions = [
      {
        name: 'pros',
        label: `Pros of working as ${jobTitleArticle} ${jobTitle}`,
      },
      {
        name: 'cons',
        label: `Cons of working as ${jobTitleArticle} ${jobTitle}`,
      },
    ];

    return questions.map(renderQuestionBox);
  };

  return (
    <div
      id="companyReviewForm"
      className={classnames(
        className,
        'z-p-20',
        popup ? '' : 'rounded',
        popup ? classes['form-container-popup'] : classes['form-container']
      )}
    >
      {!submitted ? (
        <form>
          {popup && (
            <h3 className={classnames('z-mb-36 font-weight-600 z-font-20')}>{getTitle()}</h3>
          )}
          <div
            className={classnames(
              'd-flex flex-wrap justify-content-between align-items-center z-mb-20',
              classes['star-ratings-container']
            )}
          >
            {categories.map((category) => (
              <div
                className={classnames(
                  'd-flex justify-content-between z-mb-20',
                  classes['rating-container']
                )}
                key={category.id}
              >
                <span>{category.key === 'RATING' ? 'Overall Rating*' : category.value}</span>
                <Ratings
                  smallStar={true}
                  customClasses={{ star: 'emptyStar', container: 'unsetJustify' }}
                  colorClass={'blue'}
                  ratingIndex={responses[category.id] - 1}
                  onRate={(rating) => handleRating(rating, category.id)}
                />
              </div>
            ))}
          </div>
          {jobTitle ? getJobTitleQuestions() : getCompanyQuestions()}
          {error && (
            <div className="show-error z-mb-18">
              <span className="error">{error}</span>
            </div>
          )}
          <div className="w-100 d-flex justify-content-center">
            <ZpButton
              theme="super-primary"
              className="w-100"
              onClick={() => onSubmit()}
              disabled={!canSubmit}
            >
              Submit Anonymous Review
            </ZpButton>
          </div>

          {submitError && (
            <small className="d-block w-100 red-text text-center z-mt-16">{submitError}</small>
          )}
        </form>
      ) : (
        <article className="d-flex flex-column align-items-center">
          <div className={classnames('icon-sponsors-visa', classes['submitted-icon'])} />
          <p className="z-font-600 z-font-18">Review Submitted</p>
          {!jobTitle && (
            <p className={classnames('z-font-14 text-center', classes['submitted-text'])}>
              Thanks for helping others find the right company. Help us with another company!
            </p>
          )}
        </article>
      )}
    </div>
  );
};

export default AnonReviewForm;
